import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBusinessInfo } from '@/hooks/useBusinessInfo';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { info, waLink } = useBusinessInfo();

  const navLinks = [
    { labelKey: 'nav_home', href: '/', isPage: true },
    { labelKey: 'nav_about', href: '/about', isPage: true },
    { labelKey: 'nav_menu', href: '/menu', isPage: true },
    { labelKey: 'nav_gallery', href: '/gallery', isPage: true },
    { labelKey: 'nav_location', href: '/location', isPage: true },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (link: typeof navLinks[0]) => {
    setMenuOpen(false);
    navigate(link.href);
    window.scrollTo(0, 0);
  };

  const toggleLanguage = () => {
    const next = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(next);
  };

  const isActive = (href: string) => location.pathname === href;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <button
            onClick={() => { navigate('/'); window.scrollTo(0, 0); }}
            className="flex items-center gap-3 cursor-pointer bg-transparent border-none"
          >
            <img
              src="https://static.readdy.ai/image/3aca7b4f25b4386bcee0afd7be9c4116/d8f7f8948694e3a12ee4d9db008d7937.png"
              alt="Bucara Burger Logo"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </button>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className={`text-sm font-medium tracking-widest uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap bg-transparent border-none ${
                  isActive(link.href) ? 'text-[#F57F25]' : 'text-white/80 hover:text-[#F57F25]'
                }`}
              >
                {t(link.labelKey)}
              </button>
            ))}
          </div>

          {/* Desktop Right: Language Switcher + CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold px-3 py-2 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              aria-label="Switch language"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-global-line text-sm"></i>
              </div>
              <span>{i18n.language === 'en' ? 'ES' : 'EN'}</span>
            </button>

            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#F57F25] hover:bg-[#e06d15] text-black font-bold text-sm px-5 py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base"></i>
              {t('nav_order_whatsapp')}
            </a>
          </div>

          {/* Mobile Right: Language + Hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 bg-white/10 border border-white/20 text-white text-xs font-bold px-2.5 py-1.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              aria-label="Switch language"
            >
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-global-line text-xs"></i>
              </div>
              <span>{i18n.language === 'en' ? 'ES' : 'EN'}</span>
            </button>
            <button
              className="w-10 h-10 flex items-center justify-center text-white cursor-pointer bg-transparent border-none"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 flex flex-col"
          style={{ backgroundColor: '#0a0a0a' }}
        >
          {/* Top bar — logo + close */}
          <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
            <button
              onClick={() => { setMenuOpen(false); navigate('/'); window.scrollTo(0, 0); }}
              className="flex items-center gap-2 bg-transparent border-none cursor-pointer"
            >
              <img
                src="https://static.readdy.ai/image/3aca7b4f25b4386bcee0afd7be9c4116/d8f7f8948694e3a12ee4d9db008d7937.png"
                alt="Bucara Burger"
                className="h-9 w-auto object-contain"
              />
            </button>
            <button
              className="w-10 h-10 flex items-center justify-center text-white bg-white/10 rounded-full border-none cursor-pointer"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Nav links */}
          <div className="flex-1 flex flex-col justify-center px-8 gap-1">
            {navLinks.map((link, idx) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className={`w-full flex items-center justify-between py-4 border-b cursor-pointer bg-transparent border-none ${
                  idx < navLinks.length - 1 ? 'border-white/10' : 'border-transparent'
                }`}
              >
                <span
                  className={`text-2xl font-bold tracking-widest uppercase transition-colors ${
                    isActive(link.href) ? 'text-[#F57F25]' : 'text-white'
                  }`}
                >
                  {t(link.labelKey)}
                </span>
                <div className="w-6 h-6 flex items-center justify-center">
                  <i
                    className={`ri-arrow-right-s-line text-xl ${
                      isActive(link.href) ? 'text-[#F57F25]' : 'text-white/30'
                    }`}
                  ></i>
                </div>
              </button>
            ))}
          </div>

          {/* Bottom CTA strip */}
          <div className="px-6 pb-10 pt-4 flex flex-col gap-3 border-t border-white/10">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#F57F25] text-black font-bold text-base py-4 rounded-full cursor-pointer whitespace-nowrap"
              onClick={() => setMenuOpen(false)}
            >
              <i className="ri-whatsapp-line text-lg"></i>
              {t('nav_order_whatsapp')}
            </a>
            <div className="flex items-center justify-center gap-6 pt-2">
              <a href={info.instagram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-[#F57F25] transition-colors">
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a href={info.facebook} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-[#F57F25] transition-colors">
                <i className="ri-facebook-line text-xl"></i>
              </a>
              <a href={info.tiktok} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-[#F57F25] transition-colors">
                <i className="ri-tiktok-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
